import type { PanderrTemplateDefinition } from "../types";

export const MUNICIPALITY_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "municipality-modern",
  name: "Municipality Modern",
  description:
    "An accessible, information-first civic website for towns, cities, and local government.",
  category: "municipality",

  navigation: [
    {
      label: "Government",
      href: "/government/",
      children: [
        {
          label: "Mayor & Council",
          href: "/government/mayor-council/",
        },
        {
            label: "Agendas & Minutes",
            href: "/government/agendas-minutes/",
        },
        {
          label: "Budgets & Projects",
          href: "/government/budgets-projects/",
        },
        {
          label: "Public Notices",
          href: "/government/public-notices/",
        },
        {
          label: "Open Records",
          href: "/government/open-records/",
        },
        {
          label: "Public Participation",
          href: "/government/public-participation/",
        },
      ],
    },
    { label: "Departments", href: "/departments/" },
    { label: "News & Events", href: "/news-events/" },
    { label: "About", href: "/about-us/" },
    { label: "Contact", href: "/contact-us/" },
  ],

  headerCta: {
    label: "Agendas & Minutes",
    href: "/government/agendas-minutes/",
    variant: "primary",
  },

  footer: {
    description:
      "Official information, public resources, services, and updates for our community.",
    columns: [
      {
        heading: "Government",
        links: [
          { label: "Government", href: "/government/" },
          { label: "Departments", href: "/departments/" },
           { label: "Agendas & Minutes", href: "/government/agendas-minutes/" },
        ],
      },
      {
        heading: "Resources",
        links: [
          { label: "Agendas & Minutes", href: "/government/agendas-minutes/" },
          { label: "Public Notices", href: "/government/public-notices/" },
          { label: "Contact", href: "/contact-us/" },
        ],
      },
      {
        heading: "Community",
        links: [
          { label: "Residents", href: "/about-us/" },
          { label: "Businesses", href: "/contact-us/" },
          { label: "Community Information", href: "/about-us/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "municipal-home",
      type: "municipal-home",
      eyebrow: "Meriwether County | Lone Oak, GA",
      heading: "Welcome to the Town of Lone Oak",
      body:
        "The Town of Lone Oak is committed to serving the residents of Lone Oak, GA with integrity and dedication. Our core services include community planning, public safety, waste management, and recreational facilities. We strive to enhance the quality of life for all our residents through transparent governance and efficient services. Whether you are a long-time resident or new to the area, we encourage you to explore our website to discover the many ways we can support you. Connect with us today to stay informed and involved in our community.",
      images: [
        {
          src: "https://panderr.com/api/projects/prj_850eb5decead4202b2f77901b63bec5b/assets/ast_2d9b5b06784d4a17ac52df6f0e0e923d",
          alt: "Welcome sign for the Town of Lone Oak, Georgia",
        },
      ],
      buttons: [
        {
          label: "Explore Government",
          href: "/government/",
          variant: "primary",
        },
        {
          label: "Contact Town Hall",
          href: "/contact-us/",
          variant: "secondary",
        },
        {
          label: "Read the Town Charter",
          href: "https://loneoakga.com/wp-content/uploads/2024/06/Town-Charter.pdf",
          variant: "secondary",
        },
      ],
      services: [
        {
          title: "Agendas & Minutes",
          description:
            "Review Town Council agendas, approved minutes, and public meeting records.",
           href: "/government/agendas-minutes/",
        },
        {
          title: "Public Notices",
          description:
            "Find official notices, public information, and important municipal announcements.",
            href: "/government/public-notices/",
        },
        {
          title: "Departments",
          description:
            "Find municipal departments, services, responsibilities, and contact information.",
          href: "/departments/",
        },
        {
          title: "Open Records",
          description:
            "Learn how to access public records and submit an open-records request.",
            href: "/government/open-records/",
        },
      ],
      projects: [
        {
          title: "About Lone Oak",
          description:
            "Learn more about the Town of Lone Oak, our community, local history, and the people who call it home.",
          href: "/about-us/",
        },
        {
          title: "Your Town Government",
          description:
            "Meet local elected officials and find municipal administration, public meetings, budgets, and records.",
          href: "/government/",
        },
      ],
      steps: [
        {
          title: "Town News & Events",
          description:
            "Stay current with community news, upcoming events, announcements, and municipal updates.",
        },
        {
          title: "Public Notices",
          description:
            "Review current official notices and time-sensitive information from the Town of Lone Oak.",
        },
        {
          title: "Town Council Information",
          description:
            "Find meeting information, agendas, minutes, and materials related to Town Council business.",
        },
      ],
    },
  ],

  pages: [
    {
      id: "government",
      name: "Government",
      title: "Government",
      slug: "/government/",
      sections: [
        {
          id: "government-hub",
          type: "government-hub",
          governmentPage: "overview",
          eyebrow: "Local government",
          heading: "Government Overview",
          body:
            "Find information about elected officials, public meetings, budgets, projects, public notices, public records, and ways to participate in local government.",
        },
      ],
    },

    {
      id: "mayor-council",
      name: "Mayor & Council",
      title: "Mayor & Council",
      slug: "/government/mayor-council/",
      sections: [
        {
          id: "government-mayor-council",
          type: "government-hub",
          governmentPage: "mayor-council",
          heading: "Mayor & Council",
        },
      ],
    },

    {
      id: "agendas-minutes",
      name: "Agendas & Minutes",
      title: "Agendas & Minutes",
      slug: "/government/agendas-minutes/",
      sections: [
        {
          id: "government-agendas-minutes",
          type: "government-hub",
          governmentPage: "agendas-minutes",
          heading: "Agendas & Minutes",
        },
      ],
    },

    {
      id: "budgets-projects",
      name: "Budgets & Projects",
      title: "Budgets & Projects",
      slug: "/government/budgets-projects/",
      sections: [
        {
          id: "government-budgets-projects",
          type: "government-hub",
          governmentPage: "budgets-projects",
          heading: "Budgets & Projects",
        },
      ],
    },

    {
      id: "public-notices",
      name: "Public Notices",
      title: "Public Notices",
      slug: "/government/public-notices/",
      sections: [
        {
          id: "government-public-notices",
          type: "government-hub",
          governmentPage: "public-notices",
          heading: "Public Notices",
        },
      ],
    },

    {
      id: "open-records",
      name: "Open Records",
      title: "Open Records",
      slug: "/government/open-records/",
      sections: [
        {
          id: "government-open-records",
          type: "government-hub",
          governmentPage: "open-records",
          heading: "Open Records",
        },
      ],
    },

    {
      id: "public-participation",
      name: "Public Participation",
      title: "Public Participation",
      slug: "/government/public-participation/",
      sections: [
        {
          id: "government-public-participation",
          type: "government-hub",
          governmentPage: "public-participation",
          heading: "Public Participation",
        },
      ],
    },

    {
      id: "departments",
      name: "Departments",
      title: "Departments",
      slug: "/departments/",
      sections: [
        {
          id: "departments-hero",
          type: "hero",
          eyebrow: "City departments",
          heading: "Connect with the department that can help.",
          body: "Find department responsibilities, public services, contact information, hours, and useful resources.",
          images: [
            {
              src: "/studio/assets/stock/images/small_town_corner.jpg",
              alt: "Government office workspace",
              role: "hero",
            },
          ],
        },
        {
          id: "departments-list",
          type: "services",
          eyebrow: "Departments & services",
          heading: "Local services in one place.",
          body: "Customize these cards for your municipality's departments and public-service structure.",
          services: [
            {
              title: "Administration",
              description:
                "City administration, clerk services, records, finance, and general government information.",
              href: "/contact-us/",
            },
            {
              title: "Public Works",
              description:
                "Streets, sanitation, infrastructure, utilities, facilities, and related public services.",
              href: "/contact-us/",
            },
            {
              title: "Planning & Development",
              description:
                "Planning, zoning, permits, inspections, development, and community growth resources.",
              href: "/contact-us/",
            },
          ],
        },
        {
          id: "departments-faq",
          type: "faq",
          eyebrow: "Need help?",
          heading: "Finding the right department.",
          faqs: [
            {
              question: "Who should I contact for a city service?",
              answer:
                "Use the department listings and contact information on this page to find the office responsible for the service you need.",
            },
            {
              question: "Where can I find forms and applications?",
              answer:
                "Visit Documents & Forms for downloadable applications, permits, forms, and public resources.",
            },
            {
              question: "Where can I find meeting information?",
              answer:
                "Visit Agendas & Minutes for meeting schedules, agendas, minutes, and related public records.",
            },
          ],
        },
      ],
    },

    {
      id: "agendas-minutes",
      name: "Agendas & Minutes",
      title: "Agendas & Minutes",
      slug: "/agendas-minutes/",
      sections: [
        {
          id: "meetings-hero",
          type: "hero",
          eyebrow: "Public meetings",
          heading: "Agendas, minutes, and meeting information.",
          body: "Access upcoming meeting agendas, approved minutes, meeting schedules, and other official public records.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image4.jpg",
              alt: "People reviewing meeting documents",
              role: "hero",
            },
          ],
        },
        {
          id: "meetings-services",
          type: "services",
          eyebrow: "Meeting records",
          heading: "Find public meeting information.",
          services: [
            {
              title: "Upcoming Agendas",
              description:
                "Post meeting dates, agenda packets, supporting documents, and public notices.",
              href: "/government/agendas-minutes/",
            },
            {
              title: "Approved Minutes",
              description:
                "Provide downloadable approved minutes and historical meeting records.",
              href: "/government/agendas-minutes/",
            },
            {
              title: "Meeting Calendar",
              description:
                "Share upcoming council, board, commission, and public hearing dates.",
              href: "/government/agendas-minutes/",
            },
          ],
        },
        {
          id: "meetings-faq",
          type: "faq",
          eyebrow: "Meeting information",
          heading: "Frequently requested information.",
          faqs: [
            {
              question: "When are agendas posted?",
              answer:
                "Customize this answer with your municipality's posting schedule and public notice requirements.",
            },
            {
              question: "Where can I download meeting minutes?",
              answer:
                "Approved meeting minutes can be posted and organized here for public download.",
            },
            {
              question: "How can I attend a public meeting?",
              answer:
                "Customize this answer with meeting locations, accessibility details, livestream information, and public-comment procedures.",
            },
          ],
        },
      ],
    },

    {
      id: "documents-forms",
      name: "Documents & Forms",
      title: "Documents & Forms",
      slug: "/documents-forms/",
      sections: [
        {
          id: "documents-hero",
          type: "hero",
          eyebrow: "Public documents",
          heading: "Forms, applications, notices, budgets, and public records.",
          body: "Find frequently requested documents and downloadable resources organized in one accessible location.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image1.jpg",
              alt: "Office workspace used for public records and documents",
              role: "hero",
            },
          ],
        },
        {
          id: "documents-services",
          type: "services",
          eyebrow: "Document library",
          heading: "Quick access to public resources.",
          services: [
            {
              title: "Forms & Applications",
              description:
                "Permits, applications, requests, registrations, and other commonly used forms.",
              href: "/government/agendas-minutes/",
            },
            {
              title: "Budgets & Financial Documents",
              description:
                "Approved budgets, audits, financial reports, and other fiscal documents.",
              href: "/government/budgets-projects/",
            },
            {
              title: "Public Notices",
              description:
                "Legal notices, hearings, announcements, bids, and other public postings.",
              href: "/government/public-notices/",
            },
          ],
        },
        {
          id: "documents-faq",
          type: "faq",
          eyebrow: "Document help",
          heading: "Can't find what you're looking for?",
          faqs: [
            {
              question: "Are documents available as PDFs?",
              answer:
                "Yes. Public documents can be made available as accessible downloadable PDF files.",
            },
            {
              question: "How do I request a public record?",
              answer:
                "Customize this answer with the municipality's open-records request process and contact information.",
            },
            {
              question: "Who can help me find an older document?",
              answer:
                "Provide the city clerk or records contact information for archived and historical public records.",
            },
          ],
        },
      ],
    },

    {
      id: "residents",
      name: "Residents",
      title: "Residents",
      slug: "/residents/",
      sections: [
        {
          id: "residents-hero",
          type: "hero",
          eyebrow: "Resident resources",
          heading:
            "Information and services for everyday life in our community.",
          body: "Find utilities, sanitation, permits, parks, public safety, community information, and other useful resident resources.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image3.jpg",
              alt: "Local community and city view",
              role: "hero",
            },
          ],
        },
        {
          id: "residents-services",
          type: "services",
          eyebrow: "Resident services",
          heading: "Popular community resources.",
          services: [
            {
              title: "Utilities & Services",
              description:
                "Water, sewer, sanitation, billing, service requests, and other municipal services.",
              href: "/departments/",
            },
            {
              title: "Parks & Community",
              description:
                "Parks, recreation, events, facilities, programs, and community resources.",
              href: "/about-us/",
            },
            {
              title: "Permits & Forms",
              description:
                "Find applications, permits, registrations, and frequently requested resident forms.",
              href: "/government/agendas-minutes/",
            },
          ],
        },
        {
          id: "residents-cta",
          type: "cta",
          eyebrow: "Need assistance?",
          heading: "We're here to help residents find the right resource.",
          body: "Contact the appropriate department or city office if you need help with a local service.",
          buttons: [
            {
              label: "Contact City Hall",
              href: "/contact-us/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "businesses",
      name: "Businesses",
      title: "Businesses",
      slug: "/businesses/",
      sections: [
        {
          id: "businesses-hero",
          type: "hero",
          eyebrow: "Business resources",
          heading:
            "Helping businesses start, grow, and invest in our community.",
          body: "Find licensing, permitting, planning, development, zoning, procurement, and business-related government resources.",
          images: [
            {
              src: "/studio/assets/stock/images/tim-mossholder-qvWnGmoTbik-unsplash.jpg",
              alt: "Commercial development and city view",
              role: "hero",
            },
          ],
        },
        {
          id: "business-services",
          type: "services",
          eyebrow: "Business services",
          heading: "Resources for businesses and development.",
          services: [
            {
              title: "Business Licensing",
              description:
                "License requirements, applications, renewals, and local business regulations.",
              href: "/government/agendas-minutes/",
            },
            {
              title: "Planning & Zoning",
              description:
                "Development requirements, zoning information, applications, and planning resources.",
              href: "/departments/",
            },
            {
              title: "Permits & Inspections",
              description:
                "Building permits, inspections, applications, and development-related forms.",
              href: "/government/agendas-minutes/",
            },
          ],
        },
        {
          id: "business-cta",
          type: "cta",
          eyebrow: "Planning a project?",
          heading: "Connect with the city before you get started.",
          body: "Reach out for guidance about licensing, development, permits, planning, or other local requirements.",
          buttons: [
            {
              label: "Contact the City",
              href: "/contact-us/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact",
      slug: "/contact-us/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Contact your city",
          heading: "We're here to help you find the right information.",
          body: "Contact City Hall or the appropriate department for assistance with services, records, meetings, permits, or general questions.",
          images: [
            {
              src: "/studio/assets/stock/images/tookapic-office-g11734fe79_1920_original.jpg",
              alt: "Local government office",
              role: "hero",
            },
          ],
        },
        {
          id: "contact-details",
          type: "contact",
          eyebrow: "City Hall",
          heading: "Contact information.",
          body: "Add the municipality's address, main phone number, office hours, email address, accessibility information, and department contacts in Studio.",
          buttons: [
            {
              label: "Call City Hall",
              href: "/contact-us/",
              hrefField: "site.contact.phone",
              variant: "primary",
            },
            {
              label: "Email City Hall",
              href: "/contact-us/",
              hrefField: "site.contact.email",
              variant: "secondary",
            },
          ],
        },
        {
          id: "contact-faq",
          type: "faq",
          eyebrow: "Before you contact us",
          heading: "Helpful information.",
          faqs: [
            {
              question: "What are City Hall's hours?",
              answer:
                "Add the municipality's normal business hours and holiday schedule here.",
            },
            {
              question: "How do I request an accommodation?",
              answer:
                "Add the municipality's accessibility and reasonable-accommodation contact process here.",
            },
            {
              question: "Where can I submit a public records request?",
              answer:
                "Add the municipality's open-records request instructions and records contact information here.",
            },
          ],
        },
      ],
    },
  ],
};
